import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { initTracking, trackPageView } from "../lib/gtag";
import { initUet, trackUetPageView } from "../lib/uet";
import { initOaiq } from "../lib/oaiq";
import { whenIdleOrInteraction } from "../lib/defer";
import { canTrackAds } from "../lib/consent";
import { ConsentBanner } from "../components/ConsentBanner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Nicolai Schnepf" },
      {
        name: "google-site-verification",
        content: "1gE2OYkZJOeIzdNxffz5dSjUmz-nRGt106deKrptBwk",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Shopify Freelancer | Nicolai Schnepf" },
      {
        property: "og:image",
        content: "https://shopify-freelancer.net/og-shopify-freelancer-2026.png",
      },
      {
        property: "og:image:alt",
        content:
          "shopify-freelancer.net: Nicolai Schnepf, Shopify-Entwickler und Conversion-Spezialist",
      },
      { property: "og:image:width", content: "1920" },
      { property: "og:image:height", content: "1080" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:image",
        content: "https://shopify-freelancer.net/og-shopify-freelancer-2026.png",
      },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "stylesheet", href: appCss },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://bat.bing.com" },
      { rel: "dns-prefetch", href: "https://bzrcdn.openai.com" },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Shopify Freelancer, Ratgeber und Blog",
        href: "https://shopify-freelancer.net/rss.xml",
      },
    ],
    scripts: [
      {
        // Consent Mode v2 Defaults — MUSS vor dem Google-Tag geladen werden.
        // Global: erlaubt. EEA/UK/CH/Quebec: verweigert bis Einwilligung.
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{ad_storage:'granted',ad_user_data:'granted',ad_personalization:'granted'});gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',wait_for_update:500,region:['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','IS','LI','NO','GB','CH','CA-QC']});",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "https://shopify-freelancer.net/#website",
              url: "https://shopify-freelancer.net/",
              name: "Shopify Freelancer | Nicolai Schnepf",
              inLanguage: "de-DE",
              publisher: { "@id": "https://shopify-freelancer.net/#business" },
            },
            {
              "@type": "Person",
              "@id": "https://shopify-freelancer.net/#person",
              name: "Nicolai Schnepf",
              url: "https://shopify-freelancer.net/",
              jobTitle: "Shopify-Entwickler und Conversion-Spezialist",
              image: "https://shopify-freelancer.net/og-shopify-freelancer-2026.png",
              knowsLanguage: ["de", "en"],
              knowsAbout: [
                "Shopify",
                "Shopify Theme-Entwicklung",
                "Shopify Liquid",
                "Shopify Migration",
                "Conversion-Rate-Optimierung",
                "Shopify SEO",
              ],
              worksFor: { "@id": "https://shopify-freelancer.net/#business" },
            },
            {
              "@type": ["ProfessionalService", "Organization"],
              "@id": "https://shopify-freelancer.net/#business",
              name: "Shopify Freelancer | Nicolai Schnepf",
              url: "https://shopify-freelancer.net/",
              description:
                "Freiberuflicher Shopify-Entwickler und Conversion-Spezialist: Shopify-Shop-Erstellung, Theme-Anpassungen und -Entwicklung sowie Conversion-Rate-Optimierung.",
              slogan: "Shopify-Entwicklung, die verkaufen soll, nicht nur gut aussehen.",
              areaServed: [
                { "@type": "Country", name: "Deutschland" },
                { "@type": "Country", name: "Österreich" },
                { "@type": "Country", name: "Schweiz" },
              ],
              knowsAbout: [
                "Shopify",
                "Shopify Shop-Erstellung",
                "Shopify Theme-Entwicklung",
                "Shopify Migration",
                "Shopify Conversion Optimierung",
                "Conversion-Rate-Optimierung",
                "Checkout-Optimierung",
                "Mobile Optimierung",
                "Shopify Pagespeed",
                "A/B-Testing",
                "E-Commerce Analytics",
              ],
              knowsLanguage: ["de", "en"],
              makesOffer: [
                {
                  "@type": "Offer",
                  name: "Shopify Shop-Erstellung",
                  description: "Aufbau eines Shopify-Shops zum Festpreis nach Umfang, auf Anfrage.",
                  itemOffered: {
                    "@type": "Service",
                    name: "Shopify Shop-Erstellung",
                    serviceType: "Aufbau und Umzug von Shopify-Shops",
                  },
                },
                {
                  "@type": "Offer",
                  name: "Shopify Theme-Anpassung und Entwicklung",
                  priceSpecification: {
                    "@type": "UnitPriceSpecification",
                    price: "110",
                    priceCurrency: "EUR",
                    unitText: "Stunde",
                    referenceQuantity: {
                      "@type": "QuantitativeValue",
                      value: 1,
                      unitCode: "HUR",
                    },
                  },
                  itemOffered: {
                    "@type": "Service",
                    name: "Shopify Theme-Anpassung und Entwicklung",
                    serviceType: "Theme-Anpassungen und Entwicklung für Shopify",
                  },
                },
                {
                  "@type": "Offer",
                  name: "Shopify Conversion Optimierung",
                  description: "Conversion-Rate-Optimierung für Shopify zum Festpreis ab 3.990 € netto.",
                  itemOffered: {
                    "@type": "Service",
                    name: "Shopify Conversion Optimierung",
                    serviceType: "Conversion-Rate-Optimierung für Shopify",
                  },
                  priceSpecification: {
                    "@type": "PriceSpecification",
                    minPrice: "3990",
                    priceCurrency: "EUR",
                  },
                },
              ],
            },
          ],
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="de">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Drittanbieter-Skripte laden erst nach dem ersten Rendern bzw. der ersten
  // Interaktion, das hält den kritischen Pfad für Core Web Vitals frei.
  // gtag.js lädt immer (respektiert Consent Mode). UET und OAIQ nur bei
  // erteilter Einwilligung bzw. außerhalb der Einwilligungsregionen.
  useEffect(() => {
    whenIdleOrInteraction(async () => {
      initTracking();
      const allowed = await canTrackAds();
      if (allowed) {
        initUet();
        initOaiq();
      }
    }, 2500);
  }, []);

  useEffect(() => {
    trackPageView(pathname);
    trackUetPageView();
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <ConsentBanner />
    </QueryClientProvider>
  );
}
